import React, { useState } from 'react';
import { resolveFountainOAuthHireApiBaseUrl } from 'libs/end-to-end/src/connections/fountain-oauth-hire-base-url';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';

export type FountainInstallPayload =
  | { authMethod: 'legacy_static_token'; apiKey: string }
  | {
      authMethod: 'oauth2_client_credentials';
      apiKey: string;
      apiSecret: string;
    };

export type FountainTestPayload =
  | { authMethod: 'legacy_static_token'; apiKey: string }
  | {
      authMethod: 'oauth2_client_credentials';
      apiKey: string;
      apiSecret: string;
      hireApiBaseUrl: string;
    };

export interface FountainCredentialsFormProps {
  /** From org connection settings; required to test/connect OAuth. */
  hireApiBaseUrl?: string;
  onSubmit: (payload: FountainInstallPayload) => void;
  onTest: (
    payload: FountainTestPayload,
  ) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
  error: string | null;
}

export const FountainCredentialsForm = ({
  hireApiBaseUrl,
  onSubmit,
  onTest,
  isLoading,
  error,
}: FountainCredentialsFormProps) => {
  const [authMethod, setAuthMethod] = useState<
    'legacy_static_token' | 'oauth2_client_credentials'
  >('legacy_static_token');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const buildInstallPayload = (): FountainInstallPayload | null => {
    const key = apiKey.trim();
    if (!key) {
      return null;
    }
    if (authMethod === 'legacy_static_token') {
      return { authMethod: 'legacy_static_token', apiKey: key };
    }
    const secret = apiSecret.trim();
    if (!secret) {
      return null;
    }
    return {
      authMethod: 'oauth2_client_credentials',
      apiKey: key,
      apiSecret: secret,
    };
  };

  const oauthBlocked =
    authMethod === 'oauth2_client_credentials' && !hireApiBaseUrl?.trim();

  const handleSubmit = () => {
    const payload = buildInstallPayload();
    if (!payload) {
      return;
    }
    if (oauthBlocked) {
      return;
    }
    onSubmit(payload);
  };

  const handleTest = async () => {
    const payload = buildInstallPayload();
    if (!payload) {
      return;
    }

    if (payload.authMethod === 'oauth2_client_credentials') {
      const base = hireApiBaseUrl?.trim();
      if (!base) {
        setTestResult({
          success: false,
          message:
            'OAuth requires a services API base URL (serviceworkforce or servicehire). Ask an admin to set it under Organization → Connections, then try again.',
        });
        return;
      }
    }

    setIsTesting(true);
    setTestResult(null);

    const testPayload: FountainTestPayload =
      payload.authMethod === 'legacy_static_token'
        ? payload
        : {
            ...payload,
            hireApiBaseUrl: resolveFountainOAuthHireApiBaseUrl(
              hireApiBaseUrl!.trim(),
            ),
          };

    const result = await onTest(testPayload);

    setIsTesting(false);
    setTestResult({
      success: result.success,
      message: result.success
        ? 'Connection successful! Credentials are valid.'
        : result.error || 'Connection failed',
    });
  };

  const isValid =
    apiKey.trim().length > 0 &&
    (authMethod === 'legacy_static_token' || apiSecret.trim().length > 0);

  return (
    <Spacing size="md" direction="vertical">
      <S2Typography
        variant="body-6"
        translatedContent="Choose how Sigma authenticates to Fountain: legacy static access token (X-ACCESS-TOKEN) or OAuth2 client credentials (API key + secret)."
      />

      <Spacing size="sm" direction="horizontal">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="fountain-auth"
            checked={authMethod === 'legacy_static_token'}
            onChange={() => setAuthMethod('legacy_static_token')}
            disabled={isLoading || isTesting}
          />
          <S2Typography
            variant="body-6"
            translatedContent="Legacy access token"
          />
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="fountain-auth"
            checked={authMethod === 'oauth2_client_credentials'}
            onChange={() => setAuthMethod('oauth2_client_credentials')}
            disabled={isLoading || isTesting}
          />
          <S2Typography
            variant="body-6"
            translatedContent="OAuth2 (API key + secret)"
          />
        </label>
      </Spacing>

      {authMethod === 'legacy_static_token' ? (
        <S2Typography
          variant="body-6"
          translatedContent="Uses api.fountain.com with header X-ACCESS-TOKEN."
        />
      ) : (
        <S2Typography
          variant="body-6"
          translatedContent="Uses Fountain token exchange and services APIs (serviceworkforce / servicehire) with header Application: Bearer. Consumers of retrieve-token must send Application, not Authorization."
        />
      )}

      <TextInput
        type="password"
        label={
          authMethod === 'legacy_static_token'
            ? 'Access token'
            : 'API key (client id)'
        }
        placeholder={
          authMethod === 'legacy_static_token'
            ? 'Fountain access token'
            : 'Fountain API key'
        }
        value={apiKey}
        onChange={(e) => setApiKey(e.currentTarget.value)}
        required
        disabled={isLoading || isTesting}
        data-qa="fountain-api-key-input"
      />

      {oauthBlocked && (
        <S2Typography
          variant="body-6"
          textColorVariant="red-500"
          translatedContent="OAuth requires the Fountain services API base URL in Organization → Connections (serviceworkforce or servicehire path). Save it there, then reopen this dialog."
        />
      )}

      {authMethod === 'oauth2_client_credentials' && (
        <TextInput
          type="password"
          label="API secret"
          placeholder="Fountain API secret"
          value={apiSecret}
          onChange={(e) => setApiSecret(e.currentTarget.value)}
          required
          disabled={isLoading || isTesting}
          data-qa="fountain-api-secret-input"
        />
      )}

      {testResult && (
        <S2Typography
          variant="body-6"
          textColorVariant={testResult.success ? 'green-800' : 'red-500'}
          translatedContent={testResult.message}
        />
      )}

      {error && (
        <S2Typography
          variant="body-6"
          textColorVariant="red-500"
          translatedContent={error}
        />
      )}

      <Spacing size="sm" direction="horizontal">
        <Button
          variant="outline"
          size="medium"
          data-qa="fountain-test-button"
          behavior={{
            type: 'onClick',
            onClick: () => {
              void handleTest();
            },
          }}
          disabled={!isValid || isTesting || isLoading}
        >
          {isTesting ? 'Testing...' : 'Test Connection'}
        </Button>

        <Button
          variant="primary"
          size="medium"
          data-qa="fountain-submit-button"
          behavior={{
            type: 'onClick',
            onClick: () => {
              handleSubmit();
            },
          }}
          disabled={!isValid || isLoading || isTesting || oauthBlocked}
        >
          Connect Fountain
        </Button>
      </Spacing>
    </Spacing>
  );
};
